/**
 * GMB Sync Management Page
 * 
 * Central dashboard for managing all GMB data synchronization operations.
 * Provides manual sync controls, progress monitoring, and sync history.
 */

import React, { useState, useEffect, useCallback } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Progress } from '@/components/ui/progress'
import { Separator } from '@/components/ui/separator'
import { 
  RefreshCw, 
  CheckCircle, 
  XCircle, 
  AlertCircle, 
  Clock,
  Activity,
  Database,
  Users,
  MessageSquare,
  FileText,
  BarChart,
  Loader2,
  Play,
  Pause,
  RotateCw,
  Download,
  Filter,
  Calendar,
  Settings,
  Info
} from 'lucide-react'
import { format } from 'date-fns'
import { supabase } from '@/lib/supabase'
import { SyncStatus, type GmbSyncType } from '@/components/gmb/shared/SyncStatus'
import { 
  performFullSync, 
  syncAccountsAndLocations, 
  syncReviews, 
  syncPosts, 
  syncQuestions,
  syncInsights,
  type SyncResult
} from '@/utils/gmbSync'
import type { GmbSyncLog, GmbLocation } from '@/types/gmb'

// Sync type configuration
interface SyncTypeConfig {
  key: GmbSyncType
  label: string
  description: string
  icon: React.ReactNode
  color: string
  estimatedTime: string
}

const syncTypes: SyncTypeConfig[] = [
  {
    key: 'accounts',
    label: 'Accounts & Locations',
    description: 'Sync all GMB accounts and location details',
    icon: <Database className="h-5 w-5" />,
    color: 'bg-blue-500',
    estimatedTime: '1-2 minutes'
  },
  {
    key: 'reviews',
    label: 'Reviews',
    description: 'Sync customer reviews and ratings',
    icon: <MessageSquare className="h-5 w-5" />,
    color: 'bg-green-500',
    estimatedTime: '2-5 minutes'
  },
  {
    key: 'posts',
    label: 'Posts',
    description: 'Sync all GMB posts and updates',
    icon: <FileText className="h-5 w-5" />,
    color: 'bg-purple-500',
    estimatedTime: '1-3 minutes'
  },
  {
    key: 'questions',
    label: 'Questions & Answers',
    description: 'Sync customer Q&A data',
    icon: <Users className="h-5 w-5" />,
    color: 'bg-yellow-500',
    estimatedTime: '1-2 minutes'
  },
  {
    key: 'insights',
    label: 'Insights & Metrics',
    description: 'Sync performance metrics and analytics',
    icon: <BarChart className="h-5 w-5" />,
    color: 'bg-indigo-500',
    estimatedTime: '3-5 minutes'
  }
]

// Sync status tracking
interface ActiveSync {
  type: GmbSyncType
  startedAt: Date
  progress: number
  itemsProcessed: number
  totalItems: number
  currentLocation?: string
  status: 'queued' | 'running' | 'completed' | 'failed'
  error?: string
}

export default function GMBSync() {
  // State
  const [syncLogs, setSyncLogs] = useState<GmbSyncLog[]>([])
  const [activeSyncs, setActiveSyncs] = useState<ActiveSync[]>([])
  const [selectedLocations, setSelectedLocations] = useState<string[]>([])
  const [locations, setLocations] = useState<GmbLocation[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [syncQueue, setSyncQueue] = useState<GmbSyncType[]>([])
  const [lastSyncTimes, setLastSyncTimes] = useState<Record<GmbSyncType, Date | null>>({
    accounts: null,
    locations: null,
    reviews: null,
    posts: null,
    questions: null,
    all: null
  })

  // Load initial data
  useEffect(() => {
    loadSyncData()
    const interval = setInterval(loadSyncData, 30000) // Refresh every 30 seconds
    return () => clearInterval(interval)
  }, [])

  const loadSyncData = async () => {
    try {
      // Load sync logs
      const { data: logs } = await supabase
        .from('gmb_sync_log')
        .select('*')
        .order('started_at', { ascending: false })
        .limit(100)

      if (logs) {
        setSyncLogs(logs)
        
        // Calculate last sync times
        const times: Record<string, Date | null> = {
          accounts: null,
          locations: null,
          reviews: null,
          posts: null,
          questions: null,
          all: null
        }
        
        logs.forEach(log => {
          if (log.status === 'success' && log.sync_type) {
            const syncDate = new Date(log.started_at)
            if (!times[log.sync_type] || syncDate > times[log.sync_type]!) {
              times[log.sync_type] = syncDate
            }
          }
        })
        
        setLastSyncTimes(times as any)
      }

      // Load locations
      const { data: locs } = await supabase
        .from('gmb_locations')
        .select('*')
        .eq('is_active', true)
        .order('location_name')

      if (locs) {
        setLocations(locs)
      }

      setIsLoading(false)
    } catch (error) {
      console.error('Error loading sync data:', error)
      setIsLoading(false)
    }
  }

  // Handle individual sync
  const handleSync = useCallback(async (syncType: GmbSyncType) => {
    // Add to active syncs
    const activeSync: ActiveSync = {
      type: syncType,
      startedAt: new Date(),
      progress: 0,
      itemsProcessed: 0,
      totalItems: 0,
      status: 'running'
    }
    
    setActiveSyncs(prev => [...prev, activeSync])

    try {
      let result: SyncResult

      // Simulate progress updates
      const progressInterval = setInterval(() => {
        setActiveSyncs(prev => prev.map(sync => 
          sync.type === syncType && sync.status === 'running'
            ? { ...sync, progress: Math.min(sync.progress + 10, 90) }
            : sync
        ))
      }, 1000)

      switch (syncType) {
        case 'accounts':
          result = await syncAccountsAndLocations()
          break
        case 'reviews':
          result = await syncReviews(selectedLocations.length > 0 ? selectedLocations : undefined)
          break
        case 'posts':
          result = await syncPosts(selectedLocations.length > 0 ? selectedLocations : undefined)
          break
        case 'questions':
          result = await syncQuestions(selectedLocations.length > 0 ? selectedLocations : undefined)
          break
        case 'insights':
          result = await syncInsights(selectedLocations.length > 0 ? selectedLocations : undefined)
          break
        default:
          throw new Error('Invalid sync type')
      }

      clearInterval(progressInterval)

      // Update active sync with result
      setActiveSyncs(prev => prev.map(sync => 
        sync.type === syncType
          ? {
              ...sync,
              progress: 100,
              itemsProcessed: result.itemsSynced,
              totalItems: result.itemsSynced + result.itemsFailed,
              status: result.success ? 'completed' : 'failed',
              error: result.errors.join(', ')
            }
          : sync
      ))

      // Reload sync logs
      await loadSyncData()

      // Remove from active syncs after 5 seconds
      setTimeout(() => {
        setActiveSyncs(prev => prev.filter(sync => sync.type !== syncType))
      }, 5000)

    } catch (error) {
      console.error(`Sync failed for ${syncType}:`, error)
      setActiveSyncs(prev => prev.map(sync => 
        sync.type === syncType
          ? { ...sync, status: 'failed', error: error.message }
          : sync
      ))
    }
  }, [selectedLocations])

  // Handle bulk sync
  const handleBulkSync = async (syncTypes: GmbSyncType[]) => {
    setSyncQueue(syncTypes)
    
    for (const syncType of syncTypes) {
      await handleSync(syncType)
    }
    
    setSyncQueue([])
  }

  // Handle full sync
  const handleFullSync = async () => {
    const allTypes: GmbSyncType[] = ['accounts', 'reviews', 'posts', 'questions', 'insights']
    await handleBulkSync(allTypes)
  }

  // Calculate sync statistics
  const syncStats = {
    totalSyncs: syncLogs.length,
    successfulSyncs: syncLogs.filter(log => log.status === 'success').length,
    failedSyncs: syncLogs.filter(log => log.status === 'failed').length,
    partialSyncs: syncLogs.filter(log => log.status === 'partial').length,
    totalItemsSynced: syncLogs.reduce((sum, log) => sum + log.items_synced, 0),
    totalItemsFailed: syncLogs.reduce((sum, log) => sum + log.items_failed, 0)
  }

  // Check if any sync is running
  const isSyncing = activeSyncs.some(sync => sync.status === 'running')
  const canSync = !isSyncing

  return (
    <div className="container mx-auto py-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold">GMB Sync Management</h1>
          <p className="text-muted-foreground mt-1">
            Manually sync Google My Business data and monitor sync operations
          </p>
        </div>
        
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={loadSyncData}
          >
            <RefreshCw className="h-4 w-4 mr-1" />
            Refresh
          </Button>
          
          <Button
            onClick={handleFullSync}
            disabled={!canSync}
            size="sm"
          >
            <Database className="h-4 w-4 mr-1" />
            Sync All Data
          </Button>
        </div>
      </div>

      {/* Active Syncs */}
      {activeSyncs.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Activity className="h-5 w-5 animate-pulse" />
              Active Sync Operations
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {activeSyncs.map((sync, index) => (
              <div key={`${sync.type}-${index}`} className="space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Badge variant={sync.status === 'failed' ? 'destructive' : 'default'}>
                      {sync.status === 'running' && <Loader2 className="h-3 w-3 animate-spin mr-1" />}
                      {sync.status === 'completed' && <CheckCircle className="h-3 w-3 mr-1" />}
                      {sync.status === 'failed' && <XCircle className="h-3 w-3 mr-1" />}
                      {syncTypes.find(t => t.key === sync.type)?.label}
                    </Badge>
                    {sync.currentLocation && (
                      <span className="text-sm text-muted-foreground">
                        Processing: {sync.currentLocation}
                      </span>
                    )}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {sync.itemsProcessed} / {sync.totalItems || '?'} items
                  </div>
                </div>
                <Progress value={sync.progress} className="h-2" />
                {sync.error && (
                  <Alert variant="destructive" className="py-2">
                    <AlertDescription className="text-xs">{sync.error}</AlertDescription>
                  </Alert>
                )}
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Sync Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {syncTypes.map(syncType => {
          const lastSync = lastSyncTimes[syncType.key]
          const isRunning = activeSyncs.some(s => s.type === syncType.key && s.status === 'running')
          const isQueued = syncQueue.includes(syncType.key)
          
          return (
            <Card key={syncType.key}>
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${syncType.color} text-white`}>
                      {syncType.icon}
                    </div>
                    <div>
                      <CardTitle className="text-base">{syncType.label}</CardTitle>
                      <CardDescription className="text-xs mt-0.5">
                        {syncType.description}
                      </CardDescription>
                    </div>
                  </div>
                </div>
              </CardHeader>
              
              <CardContent className="space-y-3">
                <div className="space-y-1">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Last sync:</span>
                    <span className={lastSync ? 'font-medium' : 'text-muted-foreground'}>
                      {lastSync ? format(lastSync, 'MMM d, h:mm a') : 'Never'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Est. time:</span>
                    <span>{syncType.estimatedTime}</span>
                  </div>
                </div>
                
                <Button
                  onClick={() => handleSync(syncType.key)}
                  disabled={!canSync || isRunning}
                  size="sm"
                  className="w-full"
                  variant={isQueued ? 'secondary' : 'outline'}
                >
                  {isRunning ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                      Syncing...
                    </>
                  ) : isQueued ? (
                    <>
                      <Clock className="h-4 w-4 mr-1" />
                      Queued
                    </>
                  ) : (
                    <>
                      <RefreshCw className="h-4 w-4 mr-1" />
                      Sync Now
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
          )
        })}
      </div>

      {/* Sync Statistics */}
      <Card>
        <CardHeader>
          <CardTitle>Sync Statistics</CardTitle>
          <CardDescription>
            Overall synchronization performance and history
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            <div className="text-center p-3 bg-muted rounded-lg">
              <div className="text-2xl font-bold">{syncStats.totalSyncs}</div>
              <p className="text-xs text-muted-foreground mt-1">Total Syncs</p>
            </div>
            <div className="text-center p-3 bg-green-50 dark:bg-green-950 rounded-lg">
              <div className="text-2xl font-bold text-green-600">
                {syncStats.successfulSyncs}
              </div>
              <p className="text-xs text-muted-foreground mt-1">Successful</p>
            </div>
            <div className="text-center p-3 bg-yellow-50 dark:bg-yellow-950 rounded-lg">
              <div className="text-2xl font-bold text-yellow-600">
                {syncStats.partialSyncs}
              </div>
              <p className="text-xs text-muted-foreground mt-1">Partial</p>
            </div>
            <div className="text-center p-3 bg-red-50 dark:bg-red-950 rounded-lg">
              <div className="text-2xl font-bold text-red-600">
                {syncStats.failedSyncs}
              </div>
              <p className="text-xs text-muted-foreground mt-1">Failed</p>
            </div>
            <div className="text-center p-3 bg-blue-50 dark:bg-blue-950 rounded-lg">
              <div className="text-2xl font-bold text-blue-600">
                {syncStats.totalItemsSynced.toLocaleString()}
              </div>
              <p className="text-xs text-muted-foreground mt-1">Items Synced</p>
            </div>
            <div className="text-center p-3 bg-muted rounded-lg">
              <div className="text-2xl font-bold">
                {syncStats.totalItemsFailed.toLocaleString()}
              </div>
              <p className="text-xs text-muted-foreground mt-1">Items Failed</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Sync History */}
      <Tabs defaultValue="history" className="w-full">
        <TabsList>
          <TabsTrigger value="history">Sync History</TabsTrigger>
          <TabsTrigger value="settings">Sync Settings</TabsTrigger>
        </TabsList>
        
        <TabsContent value="history" className="mt-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Recent Sync Operations</CardTitle>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm">
                    <Filter className="h-4 w-4 mr-1" />
                    Filter
                  </Button>
                  <Button variant="outline" size="sm">
                    <Download className="h-4 w-4 mr-1" />
                    Export
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <SyncStatus
                syncLogs={syncLogs}
                showDetailedLogs={true}
                onSync={handleSync}
              />
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="settings" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>Sync Configuration</CardTitle>
              <CardDescription>
                Configure sync behavior and location selection
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Location Selection */}
              <div className="space-y-2">
                <label className="text-sm font-medium">
                  Location Filter
                </label>
                <p className="text-xs text-muted-foreground">
                  Select specific locations to sync, or leave empty to sync all locations
                </p>
                <div className="border rounded-lg p-3 max-h-48 overflow-y-auto space-y-2">
                  {locations.map(location => (
                    <label key={location.id} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedLocations.includes(location.gmb_location_id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedLocations(prev => [...prev, location.gmb_location_id])
                          } else {
                            setSelectedLocations(prev => prev.filter(id => id !== location.gmb_location_id))
                          }
                        }}
                        className="rounded"
                      />
                      <span className="text-sm">{location.location_name}</span>
                      {location.franchise_region && (
                        <Badge variant="outline" className="text-xs">
                          {location.franchise_region}
                        </Badge>
                      )}
                    </label>
                  ))}
                </div>
                {selectedLocations.length > 0 && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">
                      {selectedLocations.length} location{selectedLocations.length !== 1 ? 's' : ''} selected
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setSelectedLocations([])}
                    >
                      Clear Selection
                    </Button>
                  </div>
                )}
              </div>

              <Separator />

              {/* Sync Options */}
              <div className="space-y-4">
                <h3 className="text-sm font-medium">Sync Options</h3>
                
                <Alert>
                  <Info className="h-4 w-4" />
                  <AlertTitle className="text-sm">Manual Sync Only</AlertTitle>
                  <AlertDescription className="text-xs">
                    Automatic sync scheduling is not yet enabled. All syncs must be triggered manually.
                    Future updates will include configurable auto-sync intervals.
                  </AlertDescription>
                </Alert>

                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium">Batch Processing</p>
                      <p className="text-xs text-muted-foreground">
                        Process items in batches to avoid rate limits
                      </p>
                    </div>
                    <Badge variant="outline">Enabled</Badge>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium">Error Recovery</p>
                      <p className="text-xs text-muted-foreground">
                        Automatically retry failed items
                      </p>
                    </div>
                    <Badge variant="outline">Enabled</Badge>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium">Rate Limiting</p>
                      <p className="text-xs text-muted-foreground">
                        Respect GMB API rate limits (60 requests/minute)
                      </p>
                    </div>
                    <Badge variant="outline">Enabled</Badge>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  )
}